import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
} from "@fluentui/react-components";
import {
  AttachRegular,
  DeleteRegular,
  MailRegular,
} from "@fluentui/react-icons";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "components/FluentToast";
import { VSpace } from "components/Spacer";
import { MSGraphAuth } from "api/GraphService";
import { EmailAttachmentRequest } from "api/api";
import { MSGraphParamsEmail } from "config/MSGraphConfig";
import {
  COMPANY_NUMBER_GRECO_INTERNATIONAL_LT,
  COMPANY_NUMBER_GRECO_LV,
  COMPANY_NUMBER_SAGAUTA,
  getReminderEmailTemplate,
  prepareEmailTemplate,
} from "config/reminderEmailTemplates";
import { useAppStore } from "store/AppStore";
import { useRemindersStore } from "store/RemindersStore";
import { Reminder } from "utils/types";

/** Companies whose invoice due date is taken from the reminder comment. */
const COMMENT_DUE_DATE_COMPANIES = [
  COMPANY_NUMBER_GRECO_LV,
  COMPANY_NUMBER_SAGAUTA,
  COMPANY_NUMBER_GRECO_INTERNATIONAL_LT,
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

/** Reminder document available in blob storage (referenced by URL). */
interface DocumentAttachment {
  name: string;
  url: string;
  included: boolean;
  kind: "withDiscount" | "withoutDiscount";
}

const formatAmount = (value: number) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "";
  const locale = window.navigator.languages?.[0];
  return new Intl.NumberFormat(locale, {
    useGrouping: true,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(value);
};

/** Splits a comma/semicolon separated address list and returns the first invalid one (if any). */
const findInvalidEmail = (list: string): string | null => {
  const parts = (list || "")
    .split(/[,;]/)
    .map((p) => p.trim())
    .filter((p) => p !== "");
  return parts.find((p) => !EMAIL_REGEX.test(p)) ?? null;
};

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1] || ""); // strip data:*/*;base64, prefix
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/**
 * Original file name of a reminder document as stored in blob storage
 * (last path segment of the URL, URL-decoded, without the SAS query string),
 * e.g. ".../reminders/R%C4%93%C4%B7ins%20Atl.%20-%2012345.pdf?sv=..." -> "Rēķins Atl. - 12345.pdf".
 */
const fileNameFromUrl = (url: string, fallback: string): string => {
  try {
    const path = new URL(url).pathname;
    const segment = path.substring(path.lastIndexOf("/") + 1);
    const name = decodeURIComponent(segment).trim();
    return name || fallback;
  } catch {
    return fallback;
  }
};

/**
 * Builds the list of reminder documents. The preferred document is listed first
 * and preselected; the other one is listed second and unchecked:
 * - GrECo Latvia (137): document WITHOUT discount first,
 * - Lithuanian companies (Sagauta 121, GrECo International LT 148): document WITH discount first.
 * When only one document exists it is listed and preselected regardless of company.
 */
const buildDocumentAttachments = (reminder: Reminder): DocumentAttachment[] => {
  const withDiscount: DocumentAttachment | null = reminder.reminderDocumentPath
    ? {
        name: fileNameFromUrl(
          reminder.reminderDocumentPath,
          `Reminder_${reminder.reminderNumber}.pdf`
        ),
        url: reminder.reminderDocumentPath,
        included: false,
        kind: "withDiscount",
      }
    : null;
  const withoutDiscount: DocumentAttachment | null =
    reminder.additionalReminderDocumentPath
      ? {
          name: fileNameFromUrl(
            reminder.additionalReminderDocumentPath,
            `Reminder_${reminder.reminderNumber}_without_discount.pdf`
          ),
          url: reminder.additionalReminderDocumentPath,
          included: false,
          kind: "withoutDiscount",
        }
      : null;

  const preferWithoutDiscount =
    reminder.grecoCompanyNumber === COMPANY_NUMBER_GRECO_LV;
  const ordered = (
    preferWithoutDiscount
      ? [withoutDiscount, withDiscount]
      : [withDiscount, withoutDiscount]
  ).filter((d): d is DocumentAttachment => d !== null);

  if (ordered.length > 0) ordered[0].included = true;
  return ordered;
};

export const SendReminderDialog = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [
    {
      reminders,
      reminderEntries,
      sendReminderDialog,
      sendReminderStatus,
      premiumCollectionEmailLoadStatus,
    },
    {
      setRemindersStoreValue,
      sendReminder,
      loadPremiumCollectionEmailAddress,
      loadReminders,
    },
  ] = useRemindersStore();
  const [{ appState, taxonomy, grecoCompanies, updatedGrecoCompanies }] =
    useAppStore();

  const reminder: Reminder | undefined = useMemo(
    () =>
      reminders.find(
        (r: Reminder) => r.reminderId === sendReminderDialog.reminderId
      ),
    [reminders, sendReminderDialog.reminderId]
  );

  const user = appState?.user ?? {};
  const fromName: string = user.displayName ?? "";
  const fromEmail: string = user.mail ?? user.userPrincipalName ?? "";
  const userPhone: string =
    (Array.isArray(user.businessPhones) && user.businessPhones[0]) ||
    user.mobilePhone ||
    "";

  const [toEmail, setToEmail] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [documentAttachments, setDocumentAttachments] = useState<
    DocumentAttachment[]
  >([]);
  const [additionalFiles, setAdditionalFiles] = useState<File[]>([]);
  const [emailLookupDone, setEmailLookupDone] = useState(false);
  const [emailFound, setEmailFound] = useState(true);

  const isSending = sendReminderStatus === "loading";
  const isLoadingEmail = premiumCollectionEmailLoadStatus === "loading";

  const close = () => {
    setRemindersStoreValue("sendReminderDialog", {
      isOpen: false,
      reminderId: null,
      clientId: null,
    });
  };

  // Prepare subject/body/attachments from the company template when the dialog opens
  useEffect(() => {
    if (!reminder) return;
    const currencyItem =
      taxonomy?.Currency?.byId?.[Number(reminder.currencyCode)];
    const currencyLabel = currencyItem ? t(currencyItem.code + "") : "";
    const company = (grecoCompanies ?? []).find(
      (c) => c.companyNumber === reminder.grecoCompanyNumber
    );
    const template = getReminderEmailTemplate(reminder.grecoCompanyNumber);
    // For LV and LT the invoice due date is the reminder comment (pre-filled with
    // the earliest booking date, editable by the user) or, if empty, the earliest
    // booking date of the reminder entries, while reminder.dueDate is only the
    // "To" date of the suggestion query.
    let commentDueDate = "";
    if (COMMENT_DUE_DATE_COMPANIES.includes(reminder.grecoCompanyNumber)) {
      commentDueDate = reminder.reminderComment?.trim() ?? "";
      if (!commentDueDate) {
        const earliestBookingDate = (reminderEntries ?? [])
          .filter((e) => e.reminderId === reminder.reminderId && e.bookingDate)
          .map((e) => e.bookingDate)
          .sort((a, b) => moment(a).valueOf() - moment(b).valueOf())[0];
        commentDueDate = earliestBookingDate
          ? moment(earliestBookingDate).format("DD.MM.YYYY")
          : "";
      }
    }
    const prepared = prepareEmailTemplate(template, {
      Prefix: reminder.clientInfo?.isCompany ? "" : "Cien.",
      ClientName: reminder.clientInfo?.name ?? reminder.clientName ?? "",
      ReminderNumber: reminder.reminderNumber ?? "",
      DueDate: commentDueDate
        ? commentDueDate
        : reminder.dueDate
        ? moment(reminder.dueDate).format("DD.MM.YYYY")
        : "",
      Amount: formatAmount(reminder.totalAmount),
      Currency: currencyLabel,
      CompanyName: company?.companyLong ?? company?.companyShort ?? "",
      ServicePersonName: fromName,
      ServicePersonPhone: userPhone,
      ServicePersonEMail: fromEmail,
    });
    setSubject(prepared.subject);
    setBody(prepared.body);
    setDocumentAttachments(buildDocumentAttachments(reminder));
    setAdditionalFiles([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminder?.reminderId]);

  // Look up the client's premium collection email address
  useEffect(() => {
    if (!sendReminderDialog.isOpen || !reminder) return;
    setEmailLookupDone(false);
    loadPremiumCollectionEmailAddress(
      {
        clientId: sendReminderDialog.clientId,
        reminderId: sendReminderDialog.reminderId,
      },
      (email: string) => {
        setEmailFound(!!email);
        setEmailLookupDone(true);
        if (email) setToEmail((prev) => (prev ? prev : email));
      },
      () => {
        setEmailFound(false);
        setEmailLookupDone(true);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendReminderDialog.isOpen, sendReminderDialog.reminderId]);

  const handleAttachmentToggle = (index: number) => {
    setDocumentAttachments((prev) =>
      prev.map((att, i) =>
        i === index ? { ...att, included: !att.included } : att
      )
    );
  };

  const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setAdditionalFiles((prev) => [...prev, ...files]);
    // reset so the same file can be selected again
    if (event.target) event.target.value = "";
  };

  const handleRemoveAdditionalFile = (index: number) => {
    setAdditionalFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = (): boolean => {
    if (!toEmail.trim()) {
      toast.error(t("pct.sendReminderEmail.toRequired"));
      return false;
    }
    for (const list of [toEmail, cc]) {
      const invalid = findInvalidEmail(list);
      if (invalid) {
        toast.error(
          t("pct.sendReminderEmail.invalidEmail", { email: invalid })
        );
        return false;
      }
    }
    if (!subject.trim()) {
      toast.error(t("pct.sendReminderEmail.subjectRequired"));
      return false;
    }
    const plainBody = body
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();
    if (!plainBody) {
      toast.error(t("pct.sendReminderEmail.bodyRequired"));
      return false;
    }
    if (!fromEmail) {
      toast.error(t("pct.sendReminderEmail.fromMissing"));
      return false;
    }
    return true;
  };

  const handleSend = async () => {
    if (!reminder || !validate()) return;

    // Delegated Graph token (Mail.Send) so the backend sends in the user's name.
    // If it cannot be obtained we still send; the backend falls back to the
    // configured sender in non-production environments.
    let graphToken: string | undefined;
    try {
      const resp: any = await MSGraphAuth.getAccessToken(MSGraphParamsEmail);
      graphToken = resp?.accessToken;
    } catch (error) {
      console.warn("Could not acquire Graph mail token", error);
    }

    let encodedFiles: EmailAttachmentRequest[] = [];
    try {
      encodedFiles = await Promise.all(
        additionalFiles.map(async (file) => ({
          name: file.name,
          file: await readFileAsBase64(file),
        }))
      );
    } catch (error) {
      console.error("Error reading attachment files", error);
      toast.error(t("pct.sendReminderEmail.fileReadError"));
      return;
    }

    const attachments: EmailAttachmentRequest[] = [
      ...documentAttachments
        .filter((att) => att.included)
        .map((att) => ({ name: att.name, url: att.url })),
      ...encodedFiles,
    ];

    sendReminder(
      {
        clientId: sendReminderDialog.clientId,
        reminderId: sendReminderDialog.reminderId,
        graphToken,
        email: {
          from: { name: fromName, email: fromEmail },
          to: toEmail.trim(),
          cc: cc.trim() || undefined,
          subject: subject.trim(),
          body,
          attachments,
        },
      },
      () => {
        toast.success(t("greco.success"));
        close();
        loadReminders({ reset: true, delay: true, updatedGrecoCompanies });
      }
    );
  };

  if (!reminder) return null;

  const showNoEmailWarning = emailLookupDone && !emailFound && !toEmail;

  return (
    <Dialog
      open={sendReminderDialog.isOpen}
      onOpenChange={(_, data) => {
        if (!data.open && !isSending) close();
      }}
    >
      <DialogSurface style={{ maxWidth: "900px", width: "90vw" }}>
        <DialogBody>
          <DialogTitle>
            <MailRegular style={{ marginRight: 8, verticalAlign: "middle" }} />
            {t("pct.sendReminderEmail.title", {
              reminderNumber: reminder.reminderNumber,
            })}
          </DialogTitle>
          <DialogContent style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {reminder.sentAt ? (
              <>
                <MessageBar
                  data-help="reminders-send-already-sent"
                  intent="warning"
                >
                  <MessageBarBody>
                    {t("pct.sendReminderEmail.alreadySent", {
                      sentAt: moment(reminder.sentAt).format(
                        "DD.MM.YYYY HH:mm"
                      ),
                      sentTo: reminder.sentTo ?? "",
                    })}
                  </MessageBarBody>
                </MessageBar>
                <VSpace height={10} />
              </>
            ) : null}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <Field
                label={t("pct.sendReminderEmail.fromName.label")}
                required
              >
                <Input value={fromName} disabled />
              </Field>
              <Field
                label={t("pct.sendReminderEmail.fromEmail.label")}
                required
              >
                <Input type="email" value={fromEmail} disabled />
              </Field>
            </div>
            <VSpace height={10} />

            <Field
              data-help="reminders-send-to"
              label={t("pct.sendReminderEmail.to.label")}
              required
              validationState={showNoEmailWarning ? "warning" : "none"}
              validationMessage={
                showNoEmailWarning
                  ? t("pct.sendReminderEmail.noEmail.label")
                  : undefined
              }
            >
              <Input
                value={toEmail}
                onChange={(_, d) => setToEmail(d.value)}
                placeholder={t("pct.sendReminderEmail.to.placeholder")}
                disabled={isSending}
                contentAfter={isLoadingEmail ? <Spinner size="tiny" /> : null}
              />
            </Field>
            <VSpace height={10} />

            <Field
              data-help="reminders-send-cc"
              label={t("pct.sendReminderEmail.cc.label")}
            >
              <Input
                value={cc}
                onChange={(_, d) => setCc(d.value)}
                placeholder={t("pct.sendReminderEmail.cc.placeholder")}
                disabled={isSending}
              />
            </Field>
            <VSpace height={10} />

            <Field
              data-help="reminders-send-subject"
              label={t("pct.sendReminderEmail.subject.label")}
              required
            >
              <Input
                value={subject}
                onChange={(_, d) => setSubject(d.value)}
                disabled={isSending}
              />
            </Field>
            <VSpace height={10} />

            {/* Attachments section */}
            <div
              data-help="reminders-send-attachments"
              style={{
                border: "1px solid var(--colorNeutralStroke1)",
                borderRadius: 4,
                padding: 12,
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <AttachRegular />
                {t("pct.sendReminderEmail.attachments.label")}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {documentAttachments.map((attachment, index) => (
                  <div
                    key={`document-${index}`}
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Checkbox
                      checked={attachment.included}
                      onChange={() => handleAttachmentToggle(index)}
                      disabled={isSending}
                      label={
                        <span>
                          {attachment.name}
                          <span style={{ opacity: 0.7, marginLeft: 6 }}>
                            (
                            {attachment.kind === "withDiscount"
                              ? t(
                                  "pct.sendReminderEmail.attachmentWithDiscount.label"
                                )
                              : t(
                                  "pct.sendReminderEmail.attachmentWithoutDiscount.label"
                                )}
                            )
                          </span>
                        </span>
                      }
                    />
                  </div>
                ))}
                {additionalFiles.map((file, index) => (
                  <div
                    key={`additional-${index}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      paddingLeft: 6,
                    }}
                  >
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <AttachRegular style={{ opacity: 0.7 }} />
                      {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </span>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<DeleteRegular />}
                      onClick={() => handleRemoveAdditionalFile(index)}
                      disabled={isSending}
                    />
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button
                  data-help="reminders-send-add-files"
                  appearance="subtle"
                  size="small"
                  icon={<AttachRegular />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSending}
                >
                  {t("pct.sendReminderEmail.addMoreFiles.label")}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleFilesChange}
                />
              </div>
            </div>
            <VSpace height={10} />

            <Field
              data-help="reminders-send-body"
              label={t("pct.sendReminderEmail.body.label")}
              required
            >
              <div style={{ background: "#fff", color: "#000" }}>
                <ReactQuill
                  theme="snow"
                  value={body}
                  onChange={setBody}
                  modules={quillModules}
                  readOnly={isSending}
                  style={{ minHeight: 250 }}
                />
              </div>
            </Field>
            <VSpace height={10} />
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" disabled={isSending} onClick={close}>
              {t("pct.close.label")}
            </Button>
            <Button
              data-help="reminders-send-button"
              appearance="primary"
              disabled={isSending || isLoadingEmail}
              icon={
                isSending ? (
                  <Spinner size="tiny" />
                ) : (
                  <MailRegular fontSize={16} />
                )
              }
              onClick={handleSend}
            >
              {isSending
                ? t("pct.sendReminderEmail.sending.label")
                : t("pct.sendReminderEmail.send.label")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
